import { BarChart3, Boxes, Calculator, FileText, History, Lock, Package, Plus, Receipt, Truck } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { SaveButtonField, SelectField, TextField, ToggleField } from "../../web/components/fields";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { SyncBadge } from "../../web/components/SyncBadge";
import { useConfirm, useWindowConfirm } from "../../web/components/Confirm";
import { RecordWindow } from "../../web/components/RecordWindow";
import { archiveAction, syncAction } from "../../web/components/recordActions";
import { SyncNotice } from "../../web/components/SyncAction";
import { FixHere } from "../../web/components/FixHere";
import { Box, ErrorNotice, IssueChip, Issues, Notice, SectionCard, SectionHeading } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { useFreshRecord } from "../../web/lib/queries";
import { basisLabel } from "../../shared/money";
import { useMoney } from "../../web/lib/money";
import { can, freeGroupName, groupOf, groupSets, itemName, itemTax, membersOf, tagsGroupOf, useItemStats, usePriceListSaver, whyNot, type CostDraft } from "./data";
import { activeJobs, activeJobsText, ITEM_NUMBER_MAX, lockText, type MaterialRow, type PriceListAction, type PriceListData, type PricingRule, priceLock } from "./meta";
import { itemLockAction, LockedChangeNotice } from "./PriceLock";
import { GroupNameNotice, NameTags } from "./NameTags";
import { type OwnTags, splitName } from "./names";
import { itemNumberKey } from "./setup";
import { CheckNotice, type ItemDraft, itemCount, PricingTab, profitTitle, typedPriceRule } from "./PricingTab";
import { ItemStatsTab, jobDataMissing, withStatsTab } from "./StatsTab";
import { CostUpdateNotice, SupplierPricesTab, type PriceDraft, priceDraftUnsaved } from "./SupplierPricesTab";
import { useLeftUnsaved, usePriceListWindows, useUnsavedGuard } from "./windows";
import { ChangedNotice, HistoryTab } from "./HistoryTab";
import { type ItemProblemId, itemContext, itemProblems, pricesDueDetail, sharedNameText, sharedNumberText } from "./attention";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";

/*
 * One material, in a window like ServiceM8's job card: Details, Pricing, Cost (and suppliers), Stock, Stats and History tabs,
 * with Archive / Restore (and Remove from / Add to ServiceM8) in its More menu, each asked first. Fields save as you leave them, to ServiceM8 as you, except
 * its own pricing: a draft until it's saved, like a group's (kept here, so it stays while another tab
 * is open, marked on the Pricing tab; closing the window asks first, in the window).
 * Opening it fetches the item from ServiceM8, so it shows exactly what's there now.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    { id: "pricing", label: "Pricing", icon: <Calculator className="size-5" /> },
    // Its cost (id kept from when it was its supplier prices too: links and windows open it by it).
    { id: "suppliers", label: "Costing", icon: <Receipt className="size-5" /> },
    // What it's costed from: its supplier prices; a kit, its parts (Parts: itemTabs).
    { id: "sources", label: "Suppliers", icon: <Truck className="size-5" /> },
    { id: "stats", label: "Stats", icon: <BarChart3 className="size-5" /> },
    { id: "history", label: "History", icon: <History className="size-5" /> },
];

/** What's not saved yet, when leaving it asks: its pricing, its new item number, its name tags, the supplier price being added. */
function unsavedText(pricing: boolean, itemNumber: boolean, supplierPrice: boolean, nameTags = false, cost = false) {
    const things = [pricing ? "its pricing" : "", cost ? "its cost" : "", itemNumber ? "its new item number" : "", nameTags ? "its name tags" : "", supplierPrice ? "the supplier price being added" : ""].filter(Boolean);
    if (things.length === 1 && pricing) return "Your changes to its pricing aren't saved yet.";
    const listed = things.length > 1 ? `${things.slice(0, -1).join(", ")} and ${things.at(-1)}` : (things[0] ?? "");
    return `${listed.charAt(0).toUpperCase()}${listed.slice(1)} ${things.length > 1 ? "aren't" : "isn't"} saved yet.`;
}

/** What's changed on an item's tabs: refused while it's archived (seeing, stats, restoring and reviewing changes aren't). */
const ARCHIVED_LOCKS: readonly PriceListAction[] = ["items.edit", "items.pricing", "costs.edit", "groups", "prices.add", "prices.update", "suppliers.edit"];
const ARCHIVED = "It's archived: restore it to make changes.";

/** The data as an archived item's tabs see it: every change on them refused (a reason already there stays). */
function readOnlyData(data: PriceListData): PriceListData {
    const whyNot = { ...data.whyNot };
    for (const action of ARCHIVED_LOCKS) whyNot[action] ??= ARCHIVED;
    return { ...data, whyNot };
}

/** Tabs about what the business pays: only with See costs (the server leaves it out otherwise). */
const COST_TABS: ReadonlySet<string> = new Set(["pricing", "suppliers", "sources"]);

/** The tabs this person can see: Stats with See stats; Pricing, Costing and Suppliers (Parts) with See costs. */
const itemTabs = (data: PriceListData) => withStatsTab(TABS, data).filter((t) => !COST_TABS.has(t.id) || can(data, "costs.view"));

/** What each tab needs to change things on it (the window says why when it can't). */
const TAB_ACTIONS: Record<string, PriceListAction> = { details: "items.edit", pricing: "items.pricing", suppliers: "costs.edit", sources: "prices.add" };
/** What its tab's changes need: as TAB_ACTIONS, but a kit's Parts (its Suppliers tab) are its cost's. */
const tabAction = (tab: string, kit: boolean): PriceListAction => (tab === "sources" && kit ? "costs.edit" : (TAB_ACTIONS[tab] ?? "items.edit"));

/** Fields a warning at the top can take you to, and the tab each is on. */
const FOCUS_TABS = { itemNumber: "details", name: "details", cost: "suppliers" } as const;
type FocusField = keyof typeof FOCUS_TABS;

/** Where a warning at the top is put right (FixHere: the whole warning, with its buttons), and the tab each is on. */
const FIX_TABS = { pricing: "pricing", profit: "pricing", cost: "suppliers", prices: "sources", name: "details", history: "history" } as const;
type FixSpot = keyof typeof FIX_TABS;

export function ItemWindow(
    props: Readonly<{
        data: PriceListData;
        uuid: string;
        tab: string;
        onTab: (tab: string) => void;
        onClose: () => void;
    }>,
) {
    const company = useCompany();
    useFreshRecord(company.id, "material", props.uuid);
    const item = props.data.materials.find((m) => m.uuid === props.uuid);
    // Archived, its tabs are read-only (restored, they can be changed): every change on them is refused, saying why.
    const tabData = useMemo(() => (item && !item.active ? readOnlyData(props.data) : props.data), [props.data, item?.active]);
    const saver = usePriceListSaver();
    const [archiveError, setArchiveError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup).
    const [duplicating, setDuplicating] = useState(false);
    const windows = usePriceListWindows();
    const [draft, setDraft] = useState<ItemDraft | null>(null);
    // Asking about unsaved changes before leaving them (closing, or opening another item here): a popup in the window (its `Here` in it).
    const windowConfirm = useWindowConfirm();
    const [numberDraft, setNumberDraft] = useState<string | null>(null);
    // Its name tags being changed, not saved yet (its Details tab): kept while other tabs are open.
    const [tagsDraft, setTagsDraft] = useState<OwnTags | null>(null);
    // Bumped each time it asks, so the Pricing tab scrolls its draft into view.
    const [reveal, setReveal] = useState(0);
    // A warning's button (Add item number, Add cost) takes you to the field it's about, on its tab, every time it's pressed.
    const [focus, setFocus] = useState<{ field: FocusField; token: number } | null>(null);
    const goTo = (field: FocusField) => {
        props.onTab(FOCUS_TABS[field]);
        setFocus({ field, token: (focus?.token ?? 0) + 1 });
    };
    const focusOn = (field: FocusField) => (focus?.field === field ? focus.token : undefined);
    // A warning's Show takes you to where it's put right, on its tab, every time it's pressed.
    const [fix, setFix] = useState<{ spot: FixSpot; token: number } | null>(null);
    const fixToken = (spot: FixSpot) => (fix?.spot === spot ? fix.token : undefined);
    // A supplier price being added (its Suppliers tab's form): kept while other tabs are open.
    const [priceDraft, setPriceDraft] = useState<PriceDraft | null>(null);
    // Its cost being changed (its Costing tab's Cost card): kept while other tabs are open, like its pricing.
    const [costDraft, setCostDraft] = useState<CostDraft | null>(null);
    useEffect(() => {
        setDraft(null);
        setCostDraft(null);
        setNumberDraft(null);
        setTagsDraft(null);
        setPriceDraft(null);
        setFix(null);
    }, [props.uuid]);
    const edited = draft !== null;
    // A new item number typed and not saved (it saves only with its Save button): kept while other tabs are open.
    const numberUnsaved = numberDraft !== null;
    const priceUnsaved = priceDraftUnsaved(priceDraft);
    const tagsUnsaved = tagsDraft !== null;
    const costUnsaved = costDraft !== null;
    const unsaved = edited || numberUnsaved || tagsUnsaved || priceUnsaved || costUnsaved;
    // Those to show in Needs attention: once moved off their tab.
    const left = useLeftUnsaved(props.tab, {
        pricing: { unsaved: edited, tab: "pricing" },
        tags: { unsaved: tagsUnsaved, tab: "details" },
        number: { unsaved: numberUnsaved, tab: "details" },
        price: { unsaved: priceUnsaved, tab: "sources" },
        cost: { unsaved: costUnsaved, tab: "suppliers" },
    });
    const unsavedNow = unsavedText(edited, numberUnsaved, priceUnsaved, tagsUnsaved, costUnsaved);
    /**
     * Ask before leaving unsaved changes (a popup: leave them, as `label` says, or keep editing), with them (and their save
     * buttons) in view behind it: the pricing, else the item number, else the name tags, else the cost, else the supplier price.
     */
    const ask = (leave: () => void, label: string) => {
        void windowConfirm.confirm({ title: "Leave without saving?", message: unsavedNow, confirmLabel: label, cancelLabel: "Keep editing", tone: "danger" }).then(
            (yes) => yes && leave(),
        );
        if (edited) {
            props.onTab("pricing");
            setReveal((n) => n + 1);
        } else if (numberUnsaved) goTo("itemNumber");
        else if (tagsUnsaved) props.onTab("details");
        else props.onTab(costUnsaved ? "suppliers" : "sources");
    };
    // Opening another item here while this one has unsaved changes: it stays, flashes (windows.ts), and asks.
    useUnsavedGuard("item", unsaved, (proceed) => ask(proceed, "Discard and open the other item"));

    if (!item) {
        return (
            <RecordWindow kind="item" icon={<Package className="size-4" />} title="Item not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This item doesn't exist any more (it may have been deleted in ServiceM8).</Notice>
            </RecordWindow>
        );
    }
    // What needs attention on it (attention.ts): which warnings its box shows.
    const ctx = itemContext(props.data);
    const found = itemProblems(item, ctx);
    const problems = new Set(found.map((p) => p.id));
    const has = (id: ItemProblemId) => problems.has(id);
    const pricesDue = found.find((p) => p.id === "pricesDue");
    const pricesDueCount = ctx.pricesDue.get(item.uuid);
    // Its pill red when it's a red problem (attention.ts: it trips up accounting software or loses money), as its list's mark is.
    const toneOf = (...ids: ItemProblemId[]) => (found.some((p) => ids.includes(p.id) && p.danger) ? "error" : "warning");
    // The items or bundles sharing its item number, if any.
    const numberClash = ctx.numbers.clashes.get(item.uuid);
    // The other items with its name, if any.
    const nameClash = ctx.names.get(item.uuid);
    const lock = priceLock(props.data, item.uuid);
    const canEdit = can(tabData, "items.edit");
    // A kit's "Suppliers" tab is its Parts (it isn't bought as one: its cost comes from them).
    const kit = item.uuid in props.data.kitParts;
    const tabs = itemTabs(props.data).map((t) => (t.id === "sources" && kit ? { ...t, label: "Parts", icon: <Boxes className="size-5" /> } : t));
    const tab = tabs.some((t) => t.id === props.tab) ? props.tab : "details";
    // Show, for a warning put right on a tab this person has (none: just the line).
    const show = (spot: FixSpot) =>
        tabs.some((t) => t.id === FIX_TABS[spot])
            ? () => {
                  props.onTab(FIX_TABS[spot]);
                  setFix({ spot, token: (fix?.token ?? 0) + 1 });
              }
            : undefined;
    const setActive = (active: boolean) => {
        setArchiveError(null);
        saver.updateMaterial(item.uuid, { active }).catch((err: Error) => setArchiveError(err));
    };

    return (
        <RecordWindow kind="item" icon={<Package className="size-4" />} share
            title={
                <span className="inline-flex max-w-full items-center gap-2">
                    <SyncBadge synced={item.synced} onDark />
                    <span className="truncate">{itemName(props.data, item)}</span>
                    {lock ? <Lock className="size-3.5 shrink-0" aria-label={lockText(lock)} /> : null}
                </span>
            }
            label={itemName(props.data, item)}
            subtitle={item.item_number || undefined}
            tabs={tabs.map((t) => ({
                ...t,
                unsaved: (t.id === "pricing" && edited) || (t.id === "details" && (numberUnsaved || tagsUnsaved)) || (t.id === "suppliers" && costUnsaved) || (t.id === "sources" && priceUnsaved),
                locked: t.id === "pricing" && lock ? lockText(lock) : undefined,
                attention: tabAttention(t.id, found),
            }))}
            tab={tab}
            onTab={props.onTab}
            onClose={() => {
                if (unsaved) ask(props.onClose, "Discard and close");
                else props.onClose();
            }}
            actions={[
                can(props.data, "items.sync") &&
                    syncAction("item", itemName(props.data, item), item.synced, item.active, null, (on) => {
                        setArchiveError(null);
                        saver.setSync(item.uuid, on).catch((err: Error) => setArchiveError(err));
                    }),
                can(props.data, "items.pricing") &&
                    item.active &&
                    itemLockAction(props.data, item, (locked) => {
                        setArchiveError(null);
                        saver.lockPrice(item.uuid, locked).catch((err: Error) => setArchiveError(err));
                    }),
                can(props.data, "items.add") && duplicateAction("item", () => setDuplicating(true)),
                can(props.data, "items.archive") && archiveAction("item", itemName(props.data, item), item.active, setActive),
            ]}
            notices={
                <>
                    <windowConfirm.Here />
                    <ErrorNotice error={archiveError} />
                    {item.active ? null : (
                        <Notice
                            tone="warning"
                            actions={
                                can(props.data, "items.archive") ? (
                                    <button type="button" className="btn-secondary" onClick={() => setActive(true)}>
                                        Restore
                                    </button>
                                ) : null
                            }
                        >
                            This item is archived: it&apos;s hidden in ServiceM8 and can&apos;t be added to jobs. Restore it to make changes.
                        </Notice>
                    )}
                    {/* Archived says what matters: not also "only in this app". */}
                    {item.active ? <SyncNotice kind="item" synced={item.synced} restoredInServiceM8={item.restoredInServiceM8} /> : null}
                    {/* Its problems as one box, worst first (attention.ts: the same as its list row and Home): a pill each, taking you to where
                        it's put right (the field, or the whole warning with its buttons on its tab). Unknown has no such place: a line with its buttons. */}
                    <Issues
                        chips={
                            <>
                                {/* Changes not saved yet, once you've moved off their tab and until saved or cancelled (windows.ts useLeftUnsaved),
                                    first: each takes you back to its save bar (or Save button). While unsaved they stand in for the warning saving resolves. */}
                                {left.has("pricing") ? <IssueChip tone="unsaved" label="Unsaved pricing" title="Its pricing has changes: save or cancel them." onShow={show("pricing")} /> : null}
                                {left.has("tags") ? <IssueChip tone="unsaved" label="Unsaved name tags" title="Its name tags have changes: save or cancel them." onShow={show("name")} /> : null}
                                {left.has("number") ? <IssueChip tone="unsaved" label="Unsaved item number" title="Its item number has been typed but not saved: save or cancel it." onShow={() => goTo("itemNumber")} /> : null}
                                {left.has("cost") ? <IssueChip tone="unsaved" label="Unsaved cost" title="Its cost has changes: save or cancel them." onShow={() => props.onTab("suppliers")} /> : null}
                                {left.has("price") ? <IssueChip tone="unsaved" label="Unsaved supplier price" title="A supplier price is being added: add it or cancel it." onShow={() => props.onTab("sources")} /> : null}
                                {has("lockedChanged") ? <IssueChip tone="error" label="Locked price changed in ServiceM8" onShow={show("pricing")} /> : null}
                                {(has("noNumber") || has("sharedNumber")) && !numberUnsaved ? (
                                    <IssueChip tone={toneOf("noNumber", "sharedNumber")} label={numberClash ? "Shared item number" : "No item number"} title={numberClash ? sharedNumberText(numberClash, "item") : "Every item needs one."} onShow={() => goTo("itemNumber")} />
                                ) : null}
                                {nameClash ? <IssueChip label="Shared name" title={sharedNameText(nameClash)} onShow={() => goTo("name")} /> : null}
                                {has("low") ? <IssueChip tone={toneOf("low")} label={profitTitle(props.data, item)} onShow={show("profit")} /> : null}
                                {has("partLoss") ? (
                                    <IssueChip tone={toneOf("partLoss")} label="Part at or below cost" title="Its part sells for no more than it costs: only its labour makes it pay. See what it makes, on its Pricing tab." onShow={show("profit")} />
                                ) : null}
                                {has("changed") ? <IssueChip label="Changed in ServiceM8" onShow={show("history")} /> : null}
                                {has("check") && !edited ? <IssueChip label="Price to update" onShow={show("pricing")} /> : null}
                                {has("costUpdate") ? <IssueChip label="Cost to update" onShow={show("cost")} /> : null}
                                {/* Its supplier prices to check: on its Suppliers tab (each one's Checked, once it's still right). */}
                                {pricesDue && pricesDueCount ? <IssueChip label={pricesDue.text} title={pricesDueDetail(pricesDueCount)} onShow={show("prices")} /> : null}
                                {has("noCost") ? <IssueChip label="No cost" title="Its pricing works from the cost, so it can't work out a price without one" onShow={can(props.data, "costs.view") ? () => goTo("cost") : undefined} /> : null}
                                {has("names") && !tagsUnsaved ? <IssueChip label="Name to update" onShow={show("name")} /> : null}
                            </>
                        }
                    >
                        {/* Nowhere else to be put right: its buttons here. */}
                        {has("unknown") ? <UnknownNotice data={props.data} item={item} onArchive={() => setActive(false)} /> : null}
                    </Issues>
                    {/* Why this tab can't be changed, if it can't (its own permission). */}
                    {whyNot(props.data, tabAction(tab, kit)) ? <Notice>{whyNot(props.data, tabAction(tab, kit))}</Notice> : null}
                </>
            }
        >
            {/* Its tab fills the window's height, so what sits at a tab's end (mt-auto: what it makes per unit) can sit at the bottom. */}
            <div className="flex flex-1 flex-col">
                {tab === "details" && (
                    <DetailsTab
                        data={tabData}
                        item={item}
                        canEdit={canEdit}
                        canEditNumber={can(props.data, "items.edit")}
                        itemNumberFocus={focusOn("itemNumber")}
                        nameFocus={focusOn("name")}
                        nameShared={nameClash ? sharedNameText(nameClash) : undefined}
                        numberDraft={numberDraft}
                        onNumberDraft={setNumberDraft}
                        tagsDraft={tagsDraft}
                        onTagsDraft={setTagsDraft}
                        // Into another group (or none): its pricing and name tags being edited go (they were for the old one).
                        onGroupChange={() => {
                            setDraft(null);
                            setTagsDraft(null);
                        }}
                        nameFix={has("names") ? <GroupNameNotice data={props.data} item={item} /> : undefined}
                        nameFixToken={fixToken("name")}
                        // Its stock, a section of its details (a tab of its own for two fields was too much).
                        stock={<StockCard item={item} canEdit={canEdit} kit={kit} />}
                    />
                )}
                {/* Each tab's warnings in full, with their buttons, by what they're about: where a warning's Show at the top brings you. */}
                {tab === "pricing" && (
                    <PricingTab
                        data={tabData}
                        item={item}
                        draft={draft}
                        onDraft={setDraft}
                        reveal={reveal}
                        onCostTab={() => goTo("cost")}
                        onPartsTab={() => props.onTab("sources")}
                        costDraft={costDraft}
                        profitToken={fixToken("profit")}
                        fix={
                            // Its own pricing being edited: not the price check too (its save bar's Save & update price says what saving does to it).
                            has("lockedChanged") || (has("check") && !edited) ? (
                                <>
                                    {has("lockedChanged") ? <LockedChangeNotice data={props.data} item={item} /> : null}
                                    {has("check") && !edited ? <CheckNotice data={props.data} item={item} /> : null}
                                </>
                            ) : undefined
                        }
                        fixToken={fixToken("pricing")}
                    />
                )}
                {/* Its Costing tab (its cost), and what it's costed from (Suppliers, or a kit's Parts): one component, its drafts shared. */}
                {tab === "suppliers" || tab === "sources" ? (
                    <SupplierPricesTab
                        show={tab === "suppliers" ? "costing" : "sources"}
                        pricingDraft={draft}
                        data={tabData}
                        item={item}
                        costFocus={focusOn("cost")}
                        priceDraft={priceDraft}
                        onPriceDraft={setPriceDraft}
                        costDraft={costDraft}
                        onCostDraft={setCostDraft}
                        onCostTab={() => props.onTab("suppliers")}
                        costFix={has("costUpdate") ? <CostUpdateNotice data={props.data} item={item} /> : undefined}
                        costFixToken={fixToken("cost")}
                        pricesFixToken={fixToken("prices")}
                    />
                ) : null}
                {tab === "stats" && <ItemStatsTab uuid={item.uuid} />}
                {tab === "history" && (
                    <>
                        <FixHere token={fixToken("history")} className={FIX_TOP}>
                            {has("changed") ? <ChangedNotice object="material" uuid={item.uuid} data={props.data} /> : null}
                        </FixHere>
                        <HistoryTab object="material" uuid={item.uuid} data={props.data} />
                    </>
                )}
                <LeaveGuard window={["item", item.uuid]} when={unsaved} message={`${itemName(props.data, item)}: ${unsavedNow}`} />
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="item"
                        name={item.name}
                        withNumber
                        // Its supplier prices can come too (adding them needs that permission).
                        supplierPrices={can(props.data, "prices.add") ? props.data.supplierPrices.filter((p) => p.materialUuid === item.uuid).length || null : null}
                        onCreate={(copy) => saver.duplicateItem(item.uuid, copy).then((made) => windows.openItem(made.uuid))}
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </div>
        </RecordWindow>
    );
}

/** Where each problem is put right (its tab), for marking the tab: the same places its Needs attention pills take you. */
const PROBLEM_TABS: Partial<Record<ItemProblemId, string>> = {
    noNumber: "details",
    sharedNumber: "details",
    sharedName: "details",
    unknown: "details",
    names: "details",
    lockedChanged: "pricing",
    check: "pricing",
    low: "pricing",
    partLoss: "pricing",
    costUpdate: "suppliers",
    pricesDue: "sources",
    noCost: "suppliers",
    changed: "history",
};

/** A tab's mark for what on it needs attention ("!", red when any of it is wrong), saying what (on hover); none: nothing. */
function tabAttention(tab: string, found: readonly { id: ItemProblemId; text: string; danger: boolean }[]) {
    const here = found.filter((p) => PROBLEM_TABS[p.id] === tab);
    return here.length ? { danger: here.some((p) => p.danger), label: here.map((p) => p.text).join(", ") } : undefined;
}

/** A tab's warnings in full, as a section of it: lined up with the rest. */
const FIX_TOP = "space-y-2 border-b border-line px-4 py-3";

/**
 * No one has said it's a real item yet (known.ts): it was added in ServiceM8, not here, e.g. a part
 * number typed on a job. At the top of its window (only for unknown items) until someone keeps it
 * (known) or archives it (`onArchive`, as the More menu's Archive, asked first; restored, it's unknown again).
 */
function UnknownNotice({ data, item, onArchive }: Readonly<{ data: PriceListData; item: MaterialRow; onArchive: () => void }>) {
    const confirm = useConfirm();
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    return (
        <>
            <Notice
                tone="warning"
                title="Unknown item from ServiceM8."
                actions={
                    can(data, "items.review") ? (
                        <>
                            <button type="button" className="btn-secondary" onClick={() => saver.markKnown([item.uuid]).catch((err: Error) => setError(err))}>
                                Keep
                            </button>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={async () => {
                                    const archive = archiveAction("item", itemName(data, item), true, onArchive);
                                    if (!archive.confirm || (await confirm({ ...archive.confirm, tone: "danger" }))) archive.run();
                                }}
                            >
                                Archive
                            </button>
                        </>
                    ) : null
                }
            >
                Please review it, then keep or archive it.{" "}
                <span className="mt-0.5 block text-xs opacity-80">Unknown items can come from part numbers on jobs that don&apos;t match an item, or from your accounting software once invoices are approved.</span>
            </Notice>
            <ErrorNotice error={error} />
        </>
    );
}

/** Its item number is missing or shared (its pill in Needs attention says which). */
const hasNumberIssue = (data: PriceListData, item: MaterialRow) => itemProblems(item, itemContext(data)).some((p) => p.id === "noNumber" || p.id === "sharedNumber");

/** An item number: required (accounting software matches invoice lines by it), and at most ServiceM8's length. */
const itemNumberCheck = (v: string) => {
    if (!v.trim()) return "Item number is required: accounting software matches invoice lines to items by it";
    return v.length > ITEM_NUMBER_MAX ? `At most ${ITEM_NUMBER_MAX} characters` : null;
};

/**
 * Its group (or none), on its Details tab: what sets its pricing and its name tags, with Open group.
 * Picking one changes only this item, so it's done straight away: into a group, it's priced and named
 * by it (a price or name that then differs shows to update); out of one, it keeps the group's pricing as its own.
 */
function GroupField({ data, item, onChange }: Readonly<{ data: PriceListData; item: MaterialRow; onChange: () => void }>) {
    const saver = usePriceListSaver();
    const windows = usePriceListWindows();
    const group = groupOf(data, item.uuid);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const pick = (v: string) => {
        if (v === (group?.id ?? "")) return Promise.resolve();
        onChange();
        return saver.joinGroup(item.uuid, v || null);
    };
    /**
     * A group made from its pricing (as saved: a typed price is a set price), with it in it, named after it for now: its window
     * opens to rename it. Its drafts go (as joining any group).
     */
    const newGroup = () => {
        const pricing = data.itemPricing[item.uuid];
        const rule: PricingRule = pricing
            ? { mode: pricing.mode, markupValue: pricing.markupValue, basePrice: pricing.basePrice, labourMinutes: pricing.labourMinutes, labourUnit: pricing.labourUnit, roundMode: pricing.roundMode, roundTo: pricing.roundTo }
            : typedPriceRule(item.price, { includesTax: item.price_includes_taxes, tax: itemTax(data, item).tax });
        onChange();
        setCreating(true);
        setError(null);
        saver
            .createGroup(freeGroupName(data, splitName(item.name, data.nameTags).base), rule, item.uuid)
            .then((g) => windows.openGroup(g.id, { rename: true }))
            .catch((err: Error) => setError(err))
            .finally(() => setCreating(false));
    };
    return (
        <div className="space-y-1">
            {/* Its heading as Name tags has its. */}
            <SectionHeading label="Group" aside={group ? groupSets(group) : "Items in a group share its pricing and name tags."} />
            <div className="flex items-center gap-2">
                <SelectField
                    hideLabel
                    label="Group"
                    value={group?.id ?? ""}
                    disabled={!can(data, "groups")}
                    options={[{ value: "", label: "None" }, ...data.pricingGroups.map((g) => ({ value: g.id, label: `${g.name} (${itemCount(membersOf(data, g.id).length, "item")})` }))]}
                    onSave={pick}
                    className="min-w-0 flex-1"
                />
                {group ? (
                    <button type="button" className="btn-secondary shrink-0" title="Its window: its pricing, name tags and all its items" onClick={() => windows.openGroup(group.id)}>
                        Open group
                    </button>
                ) : null}
                {/* Not in one: a group from its pricing, to share it with other items. */}
                {!group && can(data, "groups") ? (
                    <button type="button" className="btn-secondary shrink-0" title="A group from its pricing (it joins it), to share with other items" disabled={creating} onClick={newGroup}>
                        <Plus className="size-4" /> New group
                    </button>
                ) : null}
            </div>
            <ErrorNotice error={error} />
        </div>
    );
}

/** Its price, to read: for someone who can't See costs (the Pricing tab, which shows its cost too, isn't there for them). */
function PriceLine({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    return (
        <div className="space-y-1">
            <span className="block text-(length:--label-text) text-muted">Price</span>
            <p className="m-0">
                <span className="tabular-nums">{fmt.money(item.price)}</span>{" "}
                <span className="text-xs text-muted">{basisLabel(item.price_includes_taxes, itemTax(data, item).rate)}</span>
            </p>
        </div>
    );
}

function DetailsTab(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        canEdit: boolean;
        /** Its item number, even archived: restoring it is refused while another item has its number, and changing this one's is the way out. */
        canEditNumber: boolean;
        itemNumberFocus?: number;
        /** Its name is another item's too (its pill in Needs attention): why, under the field, which is highlighted; Show takes the cursor to it. */
        nameShared?: string;
        nameFocus?: number;
        /** Its name's warning in full, along the bottom of its Name tags box, and Show's token. */
        nameFix?: ReactNode;
        nameFixToken?: number;
        /** Its stock section (StockCard), under its other details. */
        stock?: ReactNode;
        numberDraft: string | null;
        onNumberDraft: (draft: string | null) => void;
        /** Its name tags being changed (NameTags), kept by the window. */
        tagsDraft: OwnTags | null;
        onTagsDraft: (draft: OwnTags | null) => void;
        /** It's moving to another group, or out of one: its drafts go. */
        onGroupChange: () => void;
    }>,
) {
    const { data, item, canEdit, itemNumberFocus } = props;
    const tags = data.nameTags;
    // Its active jobs, under its item number (with job data on): the jobs a new number would differ from.
    const stats = useItemStats(item.uuid, undefined, can(data, "stats")).data;
    const active = stats && !jobDataMissing(stats.data) ? activeJobs(stats.stats) : 0;
    // Typing another number says what it would affect, before it's saved (that it's unique: beside its label).
    const numberHint = (typed: string) => {
        if (!active) return undefined;
        if (itemNumberKey(typed) === itemNumberKey(item.item_number)) return `On ${activeJobsText(active)}.`;
        const one = active === 1;
        return (
            <span className="text-warning">
                On {activeJobsText(active)}. {one ? "It keeps" : "They keep"} &quot;{item.item_number}&quot; unless changed on {one ? "it" : "them"}, and your accounting
                software may create {one ? "a duplicate item" : "duplicate items"} once approved.
            </span>
        );
    };
    const saver = usePriceListSaver();
    const save = (patch: Parameters<typeof saver.updateMaterial>[1]) => saver.updateMaterial(item.uuid, patch);
    const confirm = useConfirm();
    // Changing the number it has is held to confirm (it matters: accounting software and jobs go by it). Its first one isn't asked.
    const one = active === 1;
    const jobsNote = `On ${activeJobsText(active)}: ${one ? "it keeps" : "they keep"} ${item.item_number} unless changed on ${one ? "it" : "them"}, and your accounting software may create a duplicate item once ${one ? "it's" : "they're"} approved.`;
    const confirmNumber = (next: string) => {
        if (!item.item_number) return Promise.resolve(true);
        return confirm({
            // In its own window (no need to name it): the change, and small, what it means for its active jobs.
            title: "Change its item number?",
            message: (
                <>
                    {item.item_number} → <span className="font-semibold">{next}</span>
                </>
            ),
            detail: active ? jobsNote : undefined,
            detailTone: "warning",
            confirmLabel: "Change item number",
            hold: true,
        });
    };
    return (
        <Box>
            <TextField
                label="Name"
                value={splitName(item.name, tags).base}
                disabled={!canEdit}
                validate={(v) => (v ? null : "Name is required")}
                warning={props.nameShared !== undefined}
                hint={props.nameShared ? <span className="text-warning">{props.nameShared}</span> : undefined}
                focusToken={props.nameFocus}
                onSave={(base) => save({ nameChange: { base } })}
            />
            {/* What identifies it, under its name (its name tags after). */}
            <div className="grid gap-4 sm:grid-cols-2">
                {/* It matters (accounting software matches invoice lines by it; jobs keep the number they were given), so it saves only with Save. */}
                <SaveButtonField
                    key={item.uuid}
                    label="Item number"
                    value={item.item_number}
                    disabled={!props.canEditNumber}
                    validate={itemNumberCheck}
                    // None, or another item's too (its pill in Needs attention, at the top of the window).
                    warning={hasNumberIssue(data, item)}
                    focusToken={itemNumberFocus}
                    hint={numberHint}
                    labelAside={<span className="text-(length:--label-text) text-muted">Unique across all items.</span>}
                    draft={props.numberDraft}
                    onDraft={props.onNumberDraft}
                    confirm={confirmNumber}
                    onSave={(item_number) => save({ item_number })}
                />
                <TextField label="Barcode" value={item.barcode} disabled={!canEdit} onSave={(barcode) => save({ barcode })} />
            </div>
            {/* Its group: what sets its pricing and name tags (groups come with See costs, as its Pricing tab does). */}
            {can(data, "costs.view") ? <GroupField data={data} item={item} onChange={props.onGroupChange} /> : <PriceLine data={data} item={item} />}
            <NameTags
                // Another item: its tags shown as it starts (only those it overrides, in a group).
                key={item.uuid}
                name={item.name}
                tags={tags}
                disabled={!canEdit}
                // Its group's tags only when it sets them; else its tags are its own (its name).
                group={tagsGroupOf(data, item.uuid)}
                own={data.itemPricing[item.uuid]?.tagOverrides}
                draft={props.tagsDraft}
                onDraft={props.onTagsDraft}
                onSave={(draft, updateName) => saver.saveItemTags(item.uuid, draft, updateName)}
                fix={props.nameFix}
                fixToken={props.nameFixToken}
            />
            {/* No description, nor "use the description on invoices": the description is kept the same as the name (rules.ts save). */}
            {props.stock}
        </Box>
    );
}

/** Its stock, a section of its Details tab (as its Name tags are): whether ServiceM8 tracks it, and how many are in stock (typed over: saved to ServiceM8). */
function StockCard({ item, canEdit, kit }: Readonly<{ item: MaterialRow; canEdit: boolean; /** A kit (its cost from its parts): its stock is still its own. */ kit: boolean }>) {
    const saver = usePriceListSaver();
    const tracked = item.item_is_inventoried;
    // Short, to fit on a line under the switch.
    let how = "ServiceM8 doesn't count it.";
    if (tracked) how = kit ? "Its own stock, not its parts'." : "Counted down as it's used on jobs.";
    return (
        // Whether it's tracked on the left (what that means under it), how many in stock beside it.
        <SectionCard label="Stock">
            <div className="grid items-start gap-4 sm:grid-cols-2">
                {/* Level with the In stock box beside it (under its label), when there is one. */}
                <div className={tracked ? "sm:pt-6" : undefined}>
                    <ToggleField label="Track stock" checked={tracked} disabled={!canEdit} hint={how} onSave={(item_is_inventoried) => saver.updateMaterial(item.uuid, { item_is_inventoried })} />
                </div>
                {tracked ? (
                    <TextField
                        label="In stock"
                        value={String(item.quantity_in_stock)}
                        inputMode="decimal"
                        disabled={!canEdit}
                        validate={(v) => (Number.isFinite(Number(v)) && v !== "" ? null : "Enter a number")}
                        onSave={(v) => saver.updateMaterial(item.uuid, { quantity_in_stock: Number(v) })}
                    />
                ) : null}
            </div>
        </SectionCard>
    );
}

